import Carousel from '@molecules/Carousel/Carousel';
import MultiSearchNoResult from '@organisms/MultiSearchComponent/MultiSearchNoResult';
import ProductBeamSkeleton from '@molecules/ProductBeam/ProductBeam.skeleton';
import { useEffect, useRef, useState } from 'react';
import { getSearchCleanResults } from '@api/interfaces/productApi';
import { MULTI_SEARCH } from '@organisms/MultiSearchComponent/MultiSearchComponent';
import KEYS from '@helpers/keys';
import { trackEditWithNoResults, trackNoResults } from '@helpers/analyticsHelpers/trackMultiSearch';
import { AxfoodProductDetailsViewModel } from '@occ/api-client';
import TrackedComponent from '@organisms/TrackedComponent/TrackedComponent';
import Product from '@molecules/Product/Product';
import useProductImpressionTracking from '@hooks/useProductImpressionsTracking';
import { useAmountOfItemsPerSlide } from '@hooks/useAmountOfItemsPerSlide';
import useResponsive from '@hooks/useResponsive';

interface Props {
  searchWord: string;
  searchWordsArray: string[];
  setSearchWordsArray: (searchWordsArray: string[]) => void;
  index: number;
}

const MultiSearchResult = ({ searchWord, searchWordsArray, setSearchWordsArray, index }: Props) => {
  const [isFetching, setIsFetching] = useState(true);
  const [productBeam, setProductBeam] = useState<any>({});
  const [tempSearchValue, setTempSearchValue] = useState('');
  // TODO: Set current page is never called. This state is not needed.
  const [currentPage] = useState(1);
  const [isFirstMount, setIsFirstMount] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const amountOfItemsPerSlide = useAmountOfItemsPerSlide();
  const { isMobile } = useResponsive();
  const { onProductShown } = useProductImpressionTracking('multisearch');

  const getProductElements = (items: Array<AxfoodProductDetailsViewModel>) =>
    items.map((product, i) => (
      <TrackedComponent
        index={i}
        product={product}
        key={`multisearch-${product.code}`}
        onInView={() => onProductShown(product, i, `multisearch | ${productBeam?.freeTextSearch}`)}
      >
        <Product product={product} variant="multisearch" category={`multisearch | ${productBeam.freeTextSearch}`} />
      </TrackedComponent>
    ));

  const fetchProducts = async (searchWord: string, page: number) => {
    let foundProducts = false;
    const productResponse = await getSearchCleanResults(
      searchWord,
      page,
      amountOfItemsPerSlide * MULTI_SEARCH.AMOUNT_OF_SLIDES_FIRST_LOAD
    );
    if (productResponse.data.results !== null) foundProducts = true;

    setProductBeam(productResponse.data);
    setIsFetching(false);

    return foundProducts;
  };

  const noResultOnFocusHandler = (e: any) => {
    setTempSearchValue(e.currentTarget.textContent);
  };

  const noResultOnBlurHandler = async (e: any) => {
    const textContent = e?.currentTarget?.textContent;
    const foundProducts = await fetchProducts(textContent, 0);
    if (foundProducts) {
      const tempWordArray = [...searchWordsArray];
      const foundWord = tempWordArray.find((word) => word.includes(tempSearchValue));
      let wordIndex = 0;
      if (foundWord != null) wordIndex = tempWordArray.indexOf(foundWord);
      if (wordIndex > 0) tempWordArray[wordIndex] = textContent;
      setSearchWordsArray(tempWordArray);
    } else {
      trackEditWithNoResults(textContent);
    }
  };

  const noResultOnKeyDownHandler = (e: any) => {
    switch (e.key) {
      case KEYS.ENTER:
      case KEYS.ESCAPE:
        e.preventDefault();
        return noResultOnBlurHandler(e);
      default:
        return;
    }
  };

  useEffect(() => {
    fetchProducts(searchWord, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchWord]);

  useEffect(() => {
    if (isFirstMount && productBeam?.results === null) {
      trackNoResults(productBeam.freeTextSearch);
      setIsFirstMount(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productBeam]);

  useEffect(() => {
    if (index === 0 && isMobile) {
      if (ref?.current) ref.current.scrollIntoView({ behavior: 'smooth' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={ref}>
      {isFetching && <ProductBeamSkeleton />}

      {!isFetching && productBeam?.results?.length > 0 && (
        <Carousel
          elements={getProductElements(productBeam.results)}
          elementsPerSlide={amountOfItemsPerSlide}
          variant="multisearch"
          title={productBeam.freeTextSearch}
          key={`${productBeam.freeTextSearch}`}
          fetchMore={() => fetchProducts(productBeam.freeTextSearch, currentPage + 1)}
          pages={productBeam.pages}
        />
      )}
      {!isFetching && productBeam?.results === null && (
        <MultiSearchNoResult
          noResult={productBeam}
          noResultOnFocusHandler={noResultOnFocusHandler}
          noResultOnBlurHandler={noResultOnBlurHandler}
          noResultOnKeyDownHandler={noResultOnKeyDownHandler}
          key={`${productBeam.freeTextSearch}`}
        />
      )}
    </div>
  );
};

export default MultiSearchResult;
